import {TranslateService} from '@ngx-translate/core';
import {SbModalService} from '@app/_components/unified/sb-modal/sb-modal.service';

/**
 * What a postponement is, and what it costs, stated before it is taken.
 *
 * Both security postponements - the password change and two-factor setup - are bounded by a
 * count, not a calendar, and the person taking one has no other way to learn the bound: the
 * button says "Skip for 7 days" and nothing else. So a skip is confirmed in the design
 * system's standard confirm modal, which states the whole bargain - how long this one lasts,
 * how many are left, the most time that can be bought in total, and what happens when they
 * run out. One helper serves every place a skip can be taken from (the login page, the
 * enrolment screen, the in-app reminder), so the terms cannot drift between them.
 */
export type SkipKind = 'mfa' | 'password';

export interface SkipTerms {
  /** Days one postponement buys. */
  days: number;
  /** Postponements still available, this one included. */
  skipsLeft: number;
}

export function confirmSkip(
  modal: SbModalService, translate: TranslateService, kind: SkipKind, terms: SkipTerms
): Promise<boolean> {
  const days = Math.max(1, Number(terms.days) || 7);
  const left = Math.max(1, Number(terms.skipsLeft) || 1);
  const params = {days, left, total: days * left};
  const lines = [
    translate.instant(`AuthSecurity.skip_confirm_body_${kind}`, params),
    left === 1
      ? translate.instant('AuthSecurity.skip_confirm_last')
      : translate.instant('AuthSecurity.skip_confirm_remaining', params),
    translate.instant(`AuthSecurity.skip_confirm_after_${kind}`)
  ];
  return modal.open({
    title: translate.instant(`AuthSecurity.skip_confirm_title_${kind}`),
    // The host renders `body` as HTML; every line here is translator text, so escape it.
    body: lines.map((line) => escapeHtml(line)).join('<br><br>'),
    variant: 'confirm',
    confirmLabel: translate.instant('AuthSecurity.skip_confirm_yes', params),
    cancelLabel: translate.instant(`AuthSecurity.skip_confirm_no_${kind}`),
    size: 'md',
    // A confirmation has nothing to scroll; the scrollable body paints a stray scrollbar.
    scrollable: false
  }).result.then(() => true).catch(() => false);
}

function escapeHtml(value: string): string {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
